import React, { useState } from "react";
import { Alert, Box, Tab, Tabs, Typography } from "@mui/material";
import { useAccounting } from "services/hooks/accounting";
import { useAccountingScope } from "./useAccountingScope";
import OverviewTab from "./components/OverviewTab";
import AccountsTab from "./components/AccountsTab";
import PaymentMethodMappingsTab from "./components/PaymentMethodMappingsTab";
import PostingTemplatesTab from "./components/PostingTemplatesTab";
import JournalEntriesTab from "./components/JournalEntriesTab";
import ReportsTab from "./components/ReportsTab";
import EventLogsTab from "./components/EventLogsTab";

const TABS = [
	{ value: "overview", label: "Overview" },
	{ value: "accounts", label: "Chart of accounts" },
	{ value: "mappings", label: "Payment method mappings" },
	{ value: "templates", label: "Posting templates" },
	{ value: "journal", label: "Journal entries" },
	{ value: "reports", label: "Reports" },
	{ value: "logs", label: "Event logs" },
];

const Accounting = () => {
	const { isAdmin, isManager, requiresOrganizationSelection, sessionOrganization } =
		useAccountingScope();

	const [activeTab, setActiveTab] = useState("overview");
	const [organizationId, setOrganizationId] = useState("");

	const {
		status,
		eventTypes,
		isLoadingStatus,
		actionError,
		isSubmitting,
		fetchAccounts,
		createAccount,
		updateAccount,
		fetchPostingTemplates,
		savePostingTemplate,
		fetchPaymentMethodMappings,
		savePaymentMethodMapping,
		updatePaymentMethodMapping,
		deletePaymentMethodMapping,
		fetchJournalEntries,
		createManualJournalEntry,
		fetchTrialBalance,
		fetchGeneralLedger,
		fetchEventLogs,
		emitEvent,
	} = useAccounting();

	const orgScopeProps = {
		requiresOrganizationSelection,
		sessionOrganization,
		organizationId,
		setOrganizationId,
	};

	if (!isAdmin && !isManager) {
		return (
			<Box m={2}>
				<Alert severity="warning">
					Only platform admins and property managers can access accounting.
				</Alert>
			</Box>
		);
	}

	return (
		<Box m="20px">
			<Typography variant="h4" sx={{ mb: 1 }}>
				Accounting
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Configure chart of accounts, map payment methods, set posting templates, review journal entries, run reports, and debug auto-posting event logs.
			</Typography>

			{actionError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{actionError}
				</Alert>
			)}

			<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
				<Tabs
					value={activeTab}
					onChange={(_, value) => setActiveTab(value)}
					variant="scrollable"
					scrollButtons="auto"
				>
					{TABS.map((tab) => (
						<Tab key={tab.value} value={tab.value} label={tab.label} />
					))}
				</Tabs>
			</Box>

			{activeTab === "overview" && (
				<OverviewTab
					status={status}
					isLoadingStatus={isLoadingStatus}
					sessionOrganization={sessionOrganization}
					requiresOrganizationSelection={requiresOrganizationSelection}
				/>
			)}
			{activeTab === "accounts" && (
				<AccountsTab
					{...orgScopeProps}
					fetchAccounts={fetchAccounts}
					createAccount={createAccount}
					updateAccount={updateAccount}
					isSubmitting={isSubmitting}
				/>
			)}
			{activeTab === "mappings" && (
				<PaymentMethodMappingsTab
					{...orgScopeProps}
					fetchAccounts={fetchAccounts}
					fetchPaymentMethodMappings={fetchPaymentMethodMappings}
					savePaymentMethodMapping={savePaymentMethodMapping}
					updatePaymentMethodMapping={updatePaymentMethodMapping}
					deletePaymentMethodMapping={deletePaymentMethodMapping}
					isSubmitting={isSubmitting}
				/>
			)}
			{activeTab === "templates" && (
				<PostingTemplatesTab
					{...orgScopeProps}
					eventTypes={eventTypes}
					fetchAccounts={fetchAccounts}
					fetchPostingTemplates={fetchPostingTemplates}
					savePostingTemplate={savePostingTemplate}
					isSubmitting={isSubmitting}
				/>
			)}
			{activeTab === "journal" && (
				<JournalEntriesTab
					{...orgScopeProps}
					eventTypes={eventTypes}
					fetchAccounts={fetchAccounts}
					fetchJournalEntries={fetchJournalEntries}
					createManualJournalEntry={createManualJournalEntry}
					isSubmitting={isSubmitting}
				/>
			)}
			{activeTab === "reports" && (
				<ReportsTab
					{...orgScopeProps}
					fetchAccounts={fetchAccounts}
					fetchTrialBalance={fetchTrialBalance}
					fetchGeneralLedger={fetchGeneralLedger}
				/>
			)}
			{activeTab === "logs" && (
				<EventLogsTab
					eventTypes={eventTypes}
					fetchEventLogs={fetchEventLogs}
					emitEvent={emitEvent}
					isSubmitting={isSubmitting}
				/>
			)}
		</Box>
	);
};

export default Accounting;
